"use client"

import { Link } from "@inertiajs/react"
import {
  Edit02,
  Lock01,
  MusicNote01,
  PlayCircle,
  Plus,
  Trash01,
} from "@untitledui/icons"

import { TextHTML } from "@/components/playlists/text-html"
import { Avatar } from "@/components/ui/avatar"
import { Dropdown } from "@/components/ui/dropdown-menu"
import { ShadowWrapper } from "@/components/ui/shadow-wrapper"
import { cn } from "@/lib/utils"
import type { Playlist } from "@/types"

export interface PlaylistCardProps {
  playlist: Playlist
  showActions?: boolean
  showAddToMyPlaylist?: boolean
  onDelete?: (playlist: Playlist) => void
  breadcrumbParent?: "owned" | "playlists"
  /**
   * When set, clicking the card boots the immersive player straight away —
   * equivalent to the Play button on the playlist page — instead of opening
   * the playlist. Falls back to the playlist page when there's nothing to
   * play. Used by the discover grid.
   */
  playOnClick?: boolean
}

export function PlaylistCard({
  playlist,
  showActions = false,
  showAddToMyPlaylist = false,
  onDelete,
  playOnClick = false,
}: PlaylistCardProps) {
  const isDisabled = !playlist.published && !playlist.is_owner
  const playlistHref = `/playlists/${playlist.slug || playlist.id}`

  // A card click is "play" only when asked to and there's something to play;
  // otherwise it opens the playlist page (the immersive feed would be empty).
  const playsOnClick = playOnClick && playlist.has_playable_tracks
  const cardHref = isDisabled
    ? "#"
    : playsOnClick
      ? `${playlistHref}/play`
      : playlistHref

  return (
    <div className="group relative h-full">
      <ShadowWrapper className="h-full">
        <div
          className={cn(
            "border-secondary bg-primary relative z-1 flex h-full flex-col overflow-hidden rounded-2xl border shadow-xs transition-all duration-200 group-hover:-translate-y-0.5",
            isDisabled && "bg-secondary",
          )}
        >
          {isDisabled && (
            <div className="absolute inset-0 z-2 flex cursor-not-allowed items-center justify-center bg-black/50">
              <Lock01 aria-hidden="true" className="size-14 text-white/80" />
            </div>
          )}

          {/* Cover image */}
          <Link
            href={cardHref}
            aria-disabled={isDisabled || undefined}
            tabIndex={isDisabled ? -1 : undefined}
            aria-label={
              playsOnClick ? `Play ${playlist.title}` : playlist.title
            }
            className={cn(
              "bg-brand-50 relative aspect-video overflow-hidden",
              isDisabled && "pointer-events-none cursor-not-allowed",
            )}
          >
            {/* Play affordance: signals that a click drops straight into the
                immersive feed rather than opening the playlist page. */}
            {playsOnClick && (
              <div className="pointer-events-none absolute inset-0 z-1 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/30 group-hover:opacity-100">
                <span className="flex size-12 items-center justify-center rounded-full bg-white/95 shadow-md transition group-hover:scale-105">
                  <PlayCircle className="text-fg-brand-primary size-7" />
                </span>
              </div>
            )}
            {playlist.cover_image_url ? (
              <img
                src={playlist.cover_image_url}
                alt={playlist.title}
                loading="lazy"
                className="size-full object-cover transition duration-300 group-hover:scale-105"
              />
            ) : (
              <div className="bg-brand-50 flex size-full items-center justify-center">
                <MusicNote01
                  aria-hidden="true"
                  className="text-brand-300 size-12"
                />
              </div>
            )}

            {/* Navy scrim — anchors the brand pill and warms the photo */}
            <div
              aria-hidden="true"
              className="from-batyr-navy/70 via-batyr-navy/10 pointer-events-none absolute inset-0 bg-linear-to-t to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100"
            />

            {/* Track count — OurHerd chip: yellow surface, navy ink */}
            <span className="bg-batyr-yellow text-batyr-navy absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold shadow-sm">
              <MusicNote01 aria-hidden="true" className="size-3" />
              {playlist.tracks_count}{" "}
              {playlist.tracks_count === 1 ? "track" : "tracks"}
            </span>
          </Link>

          {showAddToMyPlaylist && !playlist.is_owner && (
            <Link
              href={`${playlistHref}/add_playlist_to_repository`}
              method="post"
              as="button"
              aria-label="Add to my library"
              className="bg-brand-500 absolute top-2 right-2 z-10 inline-flex size-7 cursor-pointer items-center justify-center rounded-full active:scale-95"
            >
              <Plus aria-hidden="true" className="size-4" />
            </Link>
          )}

          {/* Actions dropdown — revealed on hover and on keyboard focus */}
          {showActions && (
            <div className="absolute top-2 right-2 z-10 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
              <Dropdown.Root>
                <Dropdown.DotsButton className="bg-primary/80 rounded-md p-1 backdrop-blur-sm" />
                <Dropdown.Popover>
                  <Dropdown.Menu>
                    {playlist.is_owner && (
                      <>
                        <Dropdown.Item
                          icon={Edit02}
                          label="Edit"
                          href={`${playlistHref}/edit`}
                        />
                        <Dropdown.Separator />
                      </>
                    )}
                    <Dropdown.Item
                      icon={Trash01}
                      label="Delete"
                      className="hover:[&>div]:!bg-error-primary data-[focused]:[&>div]:!bg-error-primary [&_span]:!text-error-primary [&_svg]:!text-error-primary"
                      onAction={() => onDelete?.(playlist)}
                    />
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown.Root>
            </div>
          )}

          {/* Content */}
          <div className="flex flex-1 flex-col gap-2 p-4">
            <Link
              href={cardHref}
              aria-disabled={isDisabled || undefined}
              tabIndex={isDisabled ? -1 : undefined}
              className={cn(
                "text-md text-primary hover:text-brand-primary line-clamp-2 min-h-6 font-semibold break-words transition",
                isDisabled && "cursor-not-allowed",
              )}
            >
              {playlist.title}
            </Link>

            <div className="text-tertiary flex-1 text-sm [&_p]:line-clamp-3">
              {playlist.description && (
                <TextHTML
                  text={playlist.description}
                  showHtml={false}
                  maxLength={150}
                />
              )}
            </div>

            {playlist.user && (
              <div className="border-secondary mt-auto flex min-w-0 items-center gap-1.5 border-t pt-3">
                <Avatar
                  name={playlist.user.name ?? ""}
                  avatarUrl={
                    typeof playlist.user.avatar_url === "string"
                      ? playlist.user.avatar_url
                      : undefined
                  }
                  size="xxs"
                />
                <span className="text-quaternary truncate text-xs">
                  {playlist.is_owner ? "You" : `By ${playlist.user.name}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </ShadowWrapper>
    </div>
  )
}
